import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import PinAccessDialog from "../components/PinAccessDialog";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";
import { IPageable, Params } from "../types/table/interfaces.type";
import { deleteClient, getOrgClients, updateClient } from "../services/ClientsService";
import { getUserId } from "../services/authService";
import { getCachedPinAccess } from "../utils/pinAccessCache";

type AdminTab = "clients" | "finance" | "expenses" | "reports";

interface AdminClientRow {
  id: string;
  fullName: string;
  email: string;
  countryCode?: string;
  phoneNumber?: string;
  gender?: string;
  dob?: string;
  status?: string;
  fromApp?: boolean;
  adminUserId?: string;
  branchName?: string;
  businessId?: string;
  userId?: string;
}

const normalizeCountryCodeValue = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const stringValue = typeof value === "string" ? value : String(value);
  const match = stringValue.match(/^\s*(\+?\d+)/);
  const dialCode = match ? match[1] : stringValue.trim();
  if (!dialCode) return "";
  return dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
};

const normalizeGenderValue = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lower = str.toLowerCase();
  if (lower === "m" || lower === "male") return "male";
  if (lower === "f" || lower === "female") return "female";
  return str;
};

const formatDateOnly = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const str = typeof value === "string" ? value.trim() : String(value);
  if (!str) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (str.includes("T")) return str.split("T")[0];
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

const normalizeActiveStatus = (value?: unknown): "verified" | "unverified" => {
  if (typeof value === "boolean") return value ? "verified" : "unverified";
  if (typeof value === "number") return value === 1 ? "verified" : "unverified";
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "verified") return "verified";
    if (normalized === "unverified") return "unverified";
    if (normalized === "active" || normalized === "enabled" || normalized === "true") {
      return "verified";
    }
    if (
      normalized === "inactive" ||
      normalized === "disabled" ||
      normalized === "false"
    ) {
      return "unverified";
    }
  }
  return "unverified";
};

const normalizeFromApp = (value?: unknown): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "0" || normalized === "no") {
      return false;
    }
  }
  return undefined;
};

const maskClinicEmail = (email?: string): string => {
  if (!email) return "";
  return email.toLowerCase().includes("@clinic.com") ? "_" : email;
};

const AdminData: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "clients";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [fromAppFilter, setFromAppFilter] = useState<"all" | "true" | "false">(
    "all"
  );
  const [orgClients, setOrgClients] = useState<AdminClientRow[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<AdminClientRow | null>(
    null
  );
  const [clientConfirmOpen, setClientConfirmOpen] = useState(false);
  const [clientToDeleteId, setClientToDeleteId] = useState<
    string | number | null
  >(null);
  const [clientRefreshToken, setClientRefreshToken] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  const handleFinanceNav = (route: string) => {
    if (getCachedPinAccess()) {
      navigate(route);
      return;
    }
    setPendingRoute(route);
    setPinDialogOpen(true);
  };

  const handlePinVerified = () => {
    if (pendingRoute) {
      navigate(pendingRoute);
    }
    setPendingRoute(null);
  };

  const handlePinClose = () => {
    setPinDialogOpen(false);
    setPendingRoute(null);
  };

  useEffect(() => {
    const load = async () => {
      setLoadingClients(true);
      try {
        const userId = getUserId();
        const response = await getOrgClients(userId);

        const list =
          Array.isArray(response?.users) || Array.isArray(response?.data)
            ? response.users || response.data
            : Array.isArray(response)
            ? response
            : [];

        const mapped: AdminClientRow[] = list.map((item: any, idx: number) => {
          const adminUserId = item.admin_user_id || item.adminUserId || userId || "";
          const businessId = item.business_id || item.businessId || adminUserId || "";
          const branchName =
            item.business_name ||
            item.branch_name ||
            item.branchName ||
            (businessId ? `Branch ${businessId}` : "");
          const statusValue = normalizeActiveStatus(
            item.active ?? item.status ?? item.role_name
          );
          const fromAppValue = normalizeFromApp(item.fromApp ?? item.from_app);

          return {
            id: String(
              item.id || item._id || item.user_id || item.client_id || `client-${idx}`
            ),
            fullName: item.name || item.fullName || item.full_name || "Unknown",
            email: item.email || "",
            countryCode: normalizeCountryCodeValue(
              item.country_code || item.countryCode || ""
            ),
            phoneNumber: item.phone || item.mobile || item.phoneNumber || "",
            gender: normalizeGenderValue(
              item.gender ||
                item.sex ||
                item.gender_identity ||
                item.genderIdentity ||
                ""
            ),
            dob: formatDateOnly(
              item.dob ||
                item.date_of_birth ||
                item.dateOfBirth ||
                item.birth_date ||
                item.birthDate ||
                ""
            ),
            status: statusValue,
            fromApp: fromAppValue,
            adminUserId,
            branchName,
            businessId,
            userId: item.user_id || item.userId || "",
          };
        });

        setOrgClients(mapped);
      } catch {
        setOrgClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    load();
  }, [clientRefreshToken]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = orgClients;
    if (fromAppFilter !== "all") {
      const desired = fromAppFilter === "true";
      base = base.filter((c) => c.fromApp === desired);
    }
    if (!q) return base;

    return base.filter((c) =>
      [
        c.fullName,
        c.email,
        c.countryCode,
        c.phoneNumber,
        c.gender,
        c.dob,
        c.status,
        c.fromApp === undefined ? undefined : c.fromApp ? "true" : "false",
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [orgClients, search, fromAppFilter]);

  const fetchClients = useCallback(
    async (params: Params<AdminClientRow>): Promise<IPageable<AdminClientRow>> => ({
      content: filteredClients,
      number: params.page || 0,
      size: filteredClients.length || 1,
      totalElements: filteredClients.length,
      totalPages: 1,
    }),
    [filteredClients]
  );

  const exportClientCsv = useCallback(() => {
    if (!orgClients || orgClients.length === 0) return;
    const keys: (keyof AdminClientRow)[] = [
      "id",
      "fullName",
      "email",
      "countryCode",
      "phoneNumber",
      "status",
      "fromApp",
    ];
    const header = keys.join(",");
    const lines = orgClients.map((r) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-data-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [orgClients]);

  const importCsv = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (!text) return;
        const lines = text.split("\n").filter((line) => line.trim());
        if (lines.length < 2) return;
        const header = lines[0]
          .split(",")
          .map((h) => h.replace(/"/g, "").trim());
        const dataLines = lines.slice(1);
        const newRows = dataLines.map((line, idx) => {
          const values = line.split(",").map((v) => v.replace(/"/g, "").trim());
          const obj: any = {};
          header.forEach((h, i) => {
            obj[h] = values[i] || "";
          });
          const nextId =
            orgClients.length > 0
              ? Math.max(
                  ...orgClients.map((r) => Number(r.id) || 0),
                  orgClients.length + idx + 1
                ) + 1
              : idx + 1;
          obj.id = obj.id || `import-${nextId}`;
          return {
            id: String(obj.id),
            fullName: obj.fullName || obj.name || "",
            email: obj.email || "",
            countryCode: obj.countryCode || obj.country_code || "",
            phoneNumber: obj.phoneNumber || obj.phone || "",
            status: obj.status || "",
            fromApp: normalizeFromApp(obj.fromApp ?? obj.from_app),
          } as AdminClientRow;
        });
        setOrgClients((prev) => [...newRows, ...prev]);
        toast.success("CSV imported into the table");
      };
      reader.readAsText(file);
    },
    [orgClients]
  );

  const openClientEditForId = useCallback(
    (id: string | number) => {
      const found = orgClients.find((r) => String(r.id) === String(id));
      if (found) {
        setEditingClient(found);
        setClientEditOpen(true);
      }
    },
    [orgClients]
  );

  const handleClientDelete = useCallback(async () => {
    if (!clientToDeleteId) return;
    try {
      await deleteClient(String(clientToDeleteId));
      toast.success("Client deleted");
      setOrgClients((prev) =>
        prev.filter((c) => String(c.id) !== String(clientToDeleteId))
      );
    } catch (error: any) {
      console.error("Delete client failed", error);
      toast.error("Failed to delete client");
    } finally {
      setClientConfirmOpen(false);
      setClientToDeleteId(null);
    }
  }, [clientToDeleteId]);

  const handleClientEditSubmit = useCallback(
    async (values: Record<string, string>) => {
      if (!editingClient) return;
      try {
        const adminUserId = editingClient.adminUserId || getUserId() || "";
        const businessId = editingClient.businessId || adminUserId || "";
        const branchName =
          editingClient.branchName ||
          (businessId ? `Branch ${businessId}` : "");
        const active = normalizeActiveStatus(values.status || editingClient.status);
        const payload = {
          active,
          admin_user_id: adminUserId,
          branchName,
          business_id: businessId,
          country_code: normalizeCountryCodeValue(values.countryCode),
          dob: formatDateOnly(values.dob),
          email: values.email,
          gender: normalizeGenderValue(values.gender),
          id: editingClient.id,
          mobile: values.phoneNumber,
          name: values.fullName,
          user_id: adminUserId,
        };
        await updateClient(String(editingClient.id), payload);
        toast.success("Client updated");
        setClientEditOpen(false);
        setEditingClient(null);
        setClientRefreshToken((prev) => prev + 1);
      } catch (error: any) {
        console.error("Update client failed", error);
        toast.error("Failed to update client");
      }
    },
    [editingClient]
  );

  const clientActions = Actions({
    handleOpenEditModal: { handler: openClientEditForId },
    onDelete: {
      handler: (id: number | string) => {
        setClientToDeleteId(id);
        setClientConfirmOpen(true);
      },
    },
  });

  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    { key: "finance", label: "Invoices", icon: <AccountBalanceWalletOutlinedIcon /> },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";
  const clientFormFields: Field[] = [
    { name: "fullName", label: "Full Name", required: true },
    { name: "email", label: "Email", required: true },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
    },
    { name: "phoneNumber", label: "Phone Number" },
    {
      name: "gender",
      label: "Gender",
      type: "dropdown",
      options: [
        { label: "Male", value: "male" },
        { label: "Female", value: "female" },
      ],
    },
    { name: "dob", label: "Date of Birth", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Verified", value: "verified" },
        { label: "Unverified", value: "unverified" },
      ],
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        display: "flex",
        bgcolor: COLORS.MainBackground,
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <Box
        sx={{
          width: sidebarCollapsed ? 72 : 240,
          flexShrink: 0,
          borderRight: "1px solid #eee",
          bgcolor: COLORS.WhiteBackground,
          py: 2,
          px: sidebarCollapsed ? 1 : 1.5,
          transition: "width 0.2s ease",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" fontWeight={700} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "finance") {
                      handleFinanceNav("/all-data/invoices");
                    } else if (item.key === "expenses") {
                      handleFinanceNav("/all-data/expenses");
                    } else if (item.key === "reports") {
                      handleFinanceNav("/all-data/reports");
                    }
                  }}
                  sx={{
                    mb: 0.8,
                    mx: 0.2,
                    borderRadius: 2,
                    minHeight: 44,
                    px: sidebarCollapsed ? 1.4 : 2,
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                    color: selected ? COLORS.MainColor : "text.secondary",
                    bgcolor: selected
                      ? COLORS.SidebarActiveBg || "rgba(25,118,210,0.08)"
                      : "transparent",
                    "&:hover": {
                      bgcolor: selected
                        ? COLORS.SidebarActiveBg || "rgba(25,118,210,0.12)"
                        : "rgba(0,0,0,0.03)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 0.5, sm: 1 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <CustomLabel
          text={activeTabLabel}
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 1 }}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                { label: "True", value: "true" },
                { label: "False", value: "false" },
              ],
              value: fromAppFilter,
              onChange: (v: any) =>
                setFromAppFilter(
                  v === "true" || v === "false" ? v : "all"
                ),
              label: "From App",
              placeholder: "From App",
              size: "medium",
              searchable: false,
              multiSelect: false,
              labelBgColor: COLORS.WhiteBackground,
              sx: {
                backgroundColor: COLORS.WhiteBackground,
              },
            }}
            buttonConfig={[
              {
                label: "Import CSV",
                onClick: () => fileInputRef.current?.click(),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
              {
                label: "Export CSV",
                onClick: () => exportClientCsv(),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                importCsv(file);
                e.target.value = "";
              }
            }}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminClientRow>
              filters={{ page: 0, size: 10, search }}
              clientSidePagination
              fields={[
                { key: "fullName", header: "Full Name" },
                {
                  key: "email",
                  header: "Email",
                  transform: (row) => maskClinicEmail(row.email),
                },
                { key: "countryCode", header: "Country Code" },
                { key: "phoneNumber", header: "Phone Number" },
                { key: "status", header: "Status" },
                {
                  key: "gender",
                  header: "Gender",
                  transform: (row) => {
                    const normalized = normalizeGenderValue(row.gender);
                    if (!normalized) return "";
                    return (
                      normalized.charAt(0).toUpperCase() + normalized.slice(1)
                    );
                  },
                },
                {
                  key: "dob",
                  header: "Date of Birth",
                  transform: (row) => formatDateOnly(row.dob),
                },
                {
                  key: "fromApp",
                  header: "From App",
                  transform: (row) =>
                    row.fromApp === undefined ? "-" : row.fromApp ? "True" : "False",
                },
              ]}
              fetchFunc={fetchClients}
              loading={loadingClients}
              actions={clientActions}
            />
          </Box>
        </Box>
      </Box>

      <GenericFormDialog
        open={clientEditOpen}
        onClose={() => {
          setClientEditOpen(false);
          setEditingClient(null);
        }}
        title="Edit Client"
        fields={clientFormFields}
        initialValues={
          editingClient
            ? {
                fullName: editingClient.fullName || "",
                email: editingClient.email || "",
                countryCode: normalizeCountryCodeValue(
                  editingClient.countryCode || ""
                ),
                phoneNumber: editingClient.phoneNumber || "",
                gender: normalizeGenderValue(editingClient.gender || ""),
                dob: formatDateOnly(editingClient.dob || ""),
                status: normalizeActiveStatus(editingClient.status || ""),
              }
            : undefined
        }
        onSubmit={handleClientEditSubmit}
      />

      <ConfirmDialog
        open={clientConfirmOpen}
        onClose={() => setClientConfirmOpen(false)}
        onConfirm={handleClientDelete}
        title="Confirm delete"
        message="Are you sure you want to delete this client?"
      />
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={handlePinClose}
        onVerified={handlePinVerified}
      />
    </Box>
  );
};

export default AdminData;
